<script setup lang="ts">
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { WifiOffIcon } from "lucide-vue-next";
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import { probeSystemStatus } from "../queries/portal";
import WordmarkLogo from "./WordmarkLogo.vue";
import { isNativePlatform } from "../capacitor/platform";

const { t } = useI18n();
const isNative = isNativePlatform();

const RETRY_INTERVAL = 15;

const isOffline = ref(!navigator.onLine);
const isChecking = ref(false);
const countdown = ref(RETRY_INTERVAL);
let countdownInterval: ReturnType<typeof setInterval> | null = null;

function startCountdown() {
  stopCountdown();
  countdown.value = RETRY_INTERVAL;
  countdownInterval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      stopCountdown();
      checkConnection();
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
}

async function checkConnection() {
  if (isChecking.value) return;
  isChecking.value = true;
  try {
    // Bypasses the cache, since an answer from before the drop says nothing about now.
    await probeSystemStatus();
    if (isOffline.value) {
      window.location.reload();
    }
  } catch {
    startCountdown();
  } finally {
    isChecking.value = false;
  }
}

function checkNow() {
  stopCountdown();
  checkConnection();
}

function handleOffline() {
  isOffline.value = true;
}

function handleOnline() {
  if (isOffline.value) {
    checkNow(); // browser thinks we're online, verify with a ping
  }
}

watch(isOffline, (offline) => {
  if (offline) {
    startCountdown();
  } else {
    stopCountdown();
  }
});

onMounted(() => {
  window.addEventListener("offline", handleOffline);
  window.addEventListener("online", handleOnline);
  if (isOffline.value) {
    startCountdown();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("offline", handleOffline);
  window.removeEventListener("online", handleOnline);
  stopCountdown();
});
</script>

<template>
  <Transition name="offline-fade">
    <div
      v-if="isOffline"
      class="fixed inset-0 z-100 flex min-h-full flex-1 flex-col justify-center bg-gray-950 pt-24 sm:px-6 lg:px-8"
    >
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <WordmarkLogo class="mx-auto opacity-50" />
      </div>

      <div class="mt-10 flex flex-col items-center gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <div class="flex flex-col items-center gap-4">
          <div
            class="flex h-14 w-14 items-center justify-center rounded-full border border-amber-500/40 bg-amber-500/15"
          >
            <WifiOffIcon class="h-7 w-7 text-amber-500" />
          </div>

          <h1 class="text-xl font-semibold text-gray-100">
            {{ t("msg.offline") }}
          </h1>

          <p class="text-center text-sm text-gray-400">
            {{ t("msg.offlineDescription") }}
          </p>
        </div>

        <div class="w-full space-y-4">
          <div
            v-if="!isChecking && countdown > 0"
            class="rounded-md border border-amber-500/50 bg-amber-500/15 p-3 text-center"
          >
            <p class="text-sm font-medium text-amber-400">
              {{ t("msg.retryingIn") }} {{ countdown }}
              {{ countdown === 1 ? t("msg.second") : t("msg.seconds") }}
            </p>
          </div>

          <Button variant="outline" @click="checkNow" :disabled="isChecking" class="w-full text-gray-100">
            <Spinner v-if="isChecking" class="mr-2" />
            {{ isChecking ? t("msg.checkingConnection") : t("msg.tryNow") }}
          </Button>

          <!-- The overlay covers the login screen's link, so it carries its own. -->
          <div v-if="isNative" class="flex justify-center">
            <router-link
              to="/connect"
              class="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-100"
            >
              {{ t("msg.serverUrlChangeServer") }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.offline-fade-enter-active,
.offline-fade-leave-active {
  transition: opacity 0.3s ease;
}
.offline-fade-enter-from,
.offline-fade-leave-to {
  opacity: 0;
}
</style>
